// Latest-wins RPC over the generation worker with supervision: at most one
// request in flight, only the newest queued document generated next, and a
// watchdog that restarts a hung or crashed worker. The caller keeps the last
// good result on screen; a restart never blanks the canvas.

import type { AssetMap, DocumentModel, GenResult } from '../core/types'

const WATCHDOG_MS = 10000
const MAX_RESTARTS_PER_MINUTE = 3

interface Job {
  doc: DocumentModel
  assets: AssetMap
}

export class GenClient {
  private worker!: Worker
  private nextId = 1
  private inFlight = 0 // id of the request in flight, 0 when idle
  private queued: Job | null = null
  private watchdog: number | null = null
  private restartTimes: number[] = []
  private disposed = false
  onResult: ((result: GenResult) => void) | null = null
  onError: ((message: string) => void) | null = null
  onRestart: ((reason: string) => void) | null = null

  constructor() {
    this.spawn()
  }

  private spawn() {
    this.worker = new Worker(new URL('./genWorker.ts', import.meta.url), { type: 'module' })
    this.worker.onmessage = (e) => {
      const { id, ok, result, error } = e.data
      if (id !== this.inFlight) return // stale
      this.clearWatchdog()
      this.inFlight = 0
      if (ok) this.onResult?.(result)
      else this.onError?.(error)
      this.drainQueue()
    }
    this.worker.onerror = () => this.recover('crashed')
  }

  private clearWatchdog() {
    if (this.watchdog !== null) {
      clearTimeout(this.watchdog)
      this.watchdog = null
    }
  }

  private drainQueue() {
    if (this.queued) {
      const job = this.queued
      this.queued = null
      this.request(job.doc, job.assets)
    }
  }

  // Kill and respawn the worker. The document that failed is dropped (not
  // retried, it would likely fail again); a newer queued document still runs.
  private recover(reason: string) {
    if (this.disposed) return
    this.clearWatchdog()
    this.inFlight = 0
    this.worker.terminate()

    const now = Date.now()
    this.restartTimes = this.restartTimes.filter((t) => now - t < 60000)
    this.restartTimes.push(now)
    if (this.restartTimes.length > MAX_RESTARTS_PER_MINUTE) {
      this.onError?.('generation engine failed repeatedly; reload the page to continue')
      return
    }

    this.spawn()
    this.onRestart?.(reason)
    this.drainQueue()
  }

  request(doc: DocumentModel, assets: AssetMap = {}) {
    if (this.disposed) return
    if (this.inFlight !== 0) {
      this.queued = { doc, assets }
      return
    }
    this.inFlight = this.nextId++
    this.worker.postMessage({ id: this.inFlight, doc, assets })
    this.clearWatchdog()
    this.watchdog = window.setTimeout(() => this.recover('timed out'), WATCHDOG_MS)
  }

  dispose() {
    this.disposed = true
    this.clearWatchdog()
    this.worker.terminate()
  }
}
